var AccessibilityState_1;
import { __decorate, __metadata } from "tslib";
import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { PersistStateFactory } from '@core/frontend-shared';
import { Action, createSelector, Selector, State } from '@ngxs/store';
import { AccessibilityHandlerService } from './accessibility-handler.service';
import { accessibilityModuleConfigsDefaults, modulesEnabledByDefault } from './accessibility-module.interfaces';
import { AccessibilityActions } from './accessibility.actions';
import { objectCloneDeep } from '@core/shared/utils';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "./accessibility-handler.service";
const STORAGE_KEY_PAGE_STATE = 'accessibility-state';
const initialModulesConfig = Object.entries(accessibilityModuleConfigsDefaults).map(([moduleId, config]) => {
  return {
    moduleId,
    config,
    enabled: modulesEnabledByDefault.includes(moduleId)
  };
});
let AccessibilityState = /*#__PURE__*/(() => {
  let AccessibilityState = class AccessibilityState {
    static {
      AccessibilityState_1 = this;
    }
    static modules(state) {
      return state.modules;
    }
    static get(key) {
      return createSelector([AccessibilityState_1], state => {
        return typeof state[key] !== 'undefined' ? state[key] : null;
      });
    }
    constructor(persistenceFactory, handler) {
      this.persistenceFactory = persistenceFactory;
      this.handler = handler;
      this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
      this.persistedState = this.persistenceFactory.create(STORAGE_KEY_PAGE_STATE, true, 'local');
    }
    ngxsOnInit(ctx) {
      this.restorePersistedState(ctx);
    }
    restorePersistedState(ctx) {
      try {
        const persistedModules = this.persistedState.get()?.modules || [];
        const withPersistedModules = this.mergeModules(ctx.getState().modules, persistedModules);
        ctx.patchState({
          modules: withPersistedModules
        });
      } catch (err) {
        console.error('Restore of persisted AccessibilityModules failed: ', err);
      }
      ctx.dispatch(new AccessibilityActions.ApplyModules(false));
    }
    setConfigurateModule({
      getState,
      patchState,
      dispatch
    }, action) {
      const modules = getState().modules;
      let newModules;
      if (!action.config) {
        newModules = this.addOrUpdateModule(modules, action.moduleId, {
          enabled: false
        });
      } else {
        newModules = this.addOrUpdateModule(modules, action.moduleId, {
          config: action.config,
          enabled: true
        });
      }
      if (newModules !== modules) {
        // console.log('set modules to: ',newModules)
        patchState({
          modules: newModules
        });
      }
      const haveModulesOnlyBeenUpdated = newModules.length === modules.length;
      dispatch(new AccessibilityActions.ApplyModules(haveModulesOnlyBeenUpdated));
    }
    // NOTE: will return a new array of ModuleConfigEntry whenever something has changed, or *modules* argument when nothing has changed
    addOrUpdateModule(modules, moduleId, patch) {
      let found = false;
      const newModules = modules.map(m => {
        if (m.moduleId === moduleId) {
          found = true;
          const newModule = {
            ...m
          };
          if (typeof patch.enabled === 'boolean') newModule.enabled = patch.enabled;
          if (typeof patch.config === 'object' && patch.config !== null) newModule.config = {
            ...m.config,
            ...patch.config
          };
          return newModule;
        } else {
          return m;
        }
      });
      if (found) {
        return newModules;
      }
      // there is no existing entry for the module.
      // analyze patch contents to decide how to proceed.
      if (!patch.config) {
        // no need to explicitly disable a module that is not being listed
        if (!patch.enabled) return modules;
        // enable with default config
        return this.mergeModules(modules, [{
          moduleId,
          enabled: true,
          config: accessibilityModuleConfigsDefaults[moduleId]
        }]);
      } else {
        // a config was passed, add to list
        return this.mergeModules(modules, [{
          moduleId,
          enabled: true,
          ...patch
        }]);
      }
    }
    mergeModules(oldModules, newModules) {
      if (!Array.isArray(newModules)) throw new Error('mergeModules failed: newModules is no array!');
      if (!newModules.length) return oldModules;
      const filteredOldModules = oldModules.filter(m => {
        return !newModules.find(m2 => m2.moduleId === m.moduleId);
      });
      return [...filteredOldModules, ...newModules];
    }
    applyModules({
      getState,
      patchState,
      dispatch
    }, {
      haveModulesOnlyBeenUpdated
    }) {
      const modules = getState().modules;
      this.persistedState.update({
        modules
      });
      this.handler.applyModules([...modules], haveModulesOnlyBeenUpdated);
    }
    resetModules({
      getState,
      patchState,
      dispatch
    }) {
      patchState({
        modules: objectCloneDeep(initialModulesConfig)
      });
      dispatch(new AccessibilityActions.ApplyModules(false));
    }
    static {
      this.ɵfac = function AccessibilityState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccessibilityState)(i0.ɵɵinject(i1.PersistStateFactory), i0.ɵɵinject(i2.AccessibilityHandlerService));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: AccessibilityState,
        factory: AccessibilityState.ɵfac
      });
    }
  };
  __decorate([Action(AccessibilityActions.ConfigurateModule), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, AccessibilityActions.ConfigurateModule]), __metadata("design:returntype", void 0)], AccessibilityState.prototype, "setConfigurateModule", null);
  __decorate([Action(AccessibilityActions.ApplyModules), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, Object]), __metadata("design:returntype", void 0)], AccessibilityState.prototype, "applyModules", null);
  __decorate([Action(AccessibilityActions.ResetModules), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], AccessibilityState.prototype, "resetModules", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], AccessibilityState, "modules", null);
  return AccessibilityState;
})();
AccessibilityState = AccessibilityState_1 = __decorate([State({
  name: 'accessibility',
  defaults: {
    browserTheme: null,
    theme: 'auto',
    modules: objectCloneDeep(initialModulesConfig)
  }
}), __metadata("design:paramtypes", [PersistStateFactory, AccessibilityHandlerService])], AccessibilityState);
export { AccessibilityState };